import type { Pool } from "pg";

import { buildMessageVariantArmsQuery } from "./message-variant-arms-query";
import { isMessageAbTestElement, type MessageAbTestElement } from "./types";

/**
 * One A/B arm's stored content.
 *
 * `html` and `body` are separate lanes rather than coalesced: an email body
 * test writes `rendered_output.html` and an SMS/push body test writes
 * `rendered_output.body`, so collapsing them would let an arm satisfy the
 * wrong channel's check.
 */
export type VariantArmContent = {
  element: MessageAbTestElement;
  /** Set once a winner is promoted — that arm is then served to everyone. */
  promotedVariantKey: string | null;
  variantKey: string;
  name: string | null;
  metadataVariantId?: string | null;
  /** The arm's body content row; its surface keys an SMS arm's translations. */
  bodyVariantId?: string | null;
  subject: string | null;
  preheader: string | null;
  html: string | null;
  body: string | null;
  link: string | null;
  /** The stored `rendered_output.links`, read by the SMS format check. */
  links: unknown;
  /** Push arms carry the whole notification on the one `body` element: title,
   *  subtitle, and image ride `rendered_output` alongside `body`, not the
   *  subject/preheader columns (which push no longer writes). */
  title: string | null;
  subtitle: string | null;
  imageUrl: string | null;
};

/** A message's arms split by tested element; an untested element is empty. */
export type MessageVariantArms = {
  subject: VariantArmContent[];
  body: VariantArmContent[];
};

export const NO_VARIANT_ARMS: MessageVariantArms = { subject: [], body: [] };

const VARIANT_ARM_SELECT = `me.element,
            me.promoted_variant_key,
            arm.variant_key,
            arm.name,
            mcm.variant_id AS metadata_variant_id,
            mc.variant_id AS body_variant_id,
            mcm.subject AS variant_subject,
            mcm.preheader AS variant_preheader,
            mc.rendered_output->>'html' AS variant_html,
            mc.rendered_output->>'body' AS variant_body,
            mc.rendered_output->>'title' AS variant_title,
            mc.rendered_output->>'subtitle' AS variant_subtitle,
            mc.rendered_output->>'imageUrl' AS variant_image_url,
            mc.editor_state->'editorState'->>'link' AS variant_link,
            mc.rendered_output->'links' AS variant_links`;

/**
 * Every arm of every experiment linked to a message, by element. Shares
 * `buildMessageVariantArmsQuery` with the orchestrator's profile-mapping export
 * and the activation gate, so the servable set can't drift between what we
 * validate, what we export columns for, and what actually sends. Which arms a
 * recipient can draw is decided downstream (`servedVariantArms`,
 * `loadMessageExperimentStates`), not by this query.
 */
export async function loadMessageVariantArms(
  // Also called inside a transaction, where the client is the txn's, not the pool's.
  pgClient: Pick<Pool, "query">,
  messageId: string,
  workspaceId: string,
): Promise<MessageVariantArms> {
  const { rows } = await pgClient.query(
    buildMessageVariantArmsQuery(VARIANT_ARM_SELECT),
    [messageId, Number(workspaceId)],
  );
  return groupVariantArms(rows);
}

/** Exported for tests: the row → `MessageVariantArms` projection. */
export function groupVariantArms(
  rows: Array<Record<string, unknown>>,
): MessageVariantArms {
  const grouped: MessageVariantArms = { subject: [], body: [] };
  for (const row of rows) {
    const element = row.element;
    if (typeof element !== "string" || !isMessageAbTestElement(element)) {
      continue;
    }
    const arm: VariantArmContent = {
      element,
      promotedVariantKey: asStringOrNull(row.promoted_variant_key),
      variantKey: String(row.variant_key),
      name: asStringOrNull(row.name),
      metadataVariantId: asStringOrNull(row.metadata_variant_id),
      bodyVariantId: asStringOrNull(row.body_variant_id),
      subject: asStringOrNull(row.variant_subject),
      preheader: asStringOrNull(row.variant_preheader),
      html: asStringOrNull(row.variant_html),
      body: asStringOrNull(row.variant_body),
      link: asStringOrNull(row.variant_link),
      links: row.variant_links ?? null,
      title: asStringOrNull(row.variant_title),
      subtitle: asStringOrNull(row.variant_subtitle),
      imageUrl: asStringOrNull(row.variant_image_url),
    };
    if (element === "subject_preheader") {
      grouped.subject.push(arm);
    } else {
      grouped.body.push(arm);
    }
  }
  return grouped;
}

function asStringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function isFilled(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim() !== "";
}

/** How an arm is named in an error a marketer reads. */
export function variantArmLabel(
  arm: Pick<VariantArmContent, "name" | "variantKey">,
): string {
  return arm.name?.trim()
    ? arm.name.trim()
    : `Variant ${arm.variantKey.toUpperCase()}`;
}

export type FieldCoverage = {
  covered: boolean;
  /** Labels of the arms that left the field empty; empty when there is no test. */
  uncoveredVariants: string[];
};

/** One channel field to check: the base row, the arms, and the lane to read. */
export type FieldCoverageArgs = {
  base: string | null | undefined;
  arms: VariantArmContent[];
  value: (arm: VariantArmContent) => string | null;
};

/**
 * Whether a channel field is filled in for every variant that can carry it.
 *
 * Under a test every arm must carry its own copy — a populated base row does
 * **not** excuse an empty arm, because `pickVariantContent` would serve that
 * recipient the base while downstream stats still count them under their arm.
 * The composer assumes this can't happen: a new test's first arm inherits the
 * message body and the rest offer "copy content from existing variant", so an
 * empty arm is an unfinished one, never a deliberate control.
 *
 * This asks what is *configured*, not what would be *served* right now — pair
 * it with `loadMessageVariantArms`, which keeps every arm of a linked test
 * regardless of promotion or completion. Without a test there is nothing to
 * draw from and the base is required.
 */
export function checkConfiguredCoverage(
  args: FieldCoverageArgs,
): FieldCoverage {
  const { base, arms, value } = args;
  if (arms.length === 0) {
    return { covered: isFilled(base), uncoveredVariants: [] };
  }
  const uncovered = arms.filter((arm) => !isFilled(value(arm)));
  return {
    covered: uncovered.length === 0,
    uncoveredVariants: uncovered.map(variantArmLabel),
  };
}
